import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useApi } from './ApiContext';
import './Download.css';

// --- HELPER COMPONENT: DescriptionToggle ---
const DescriptionToggle = ({ text, maxLength = 80 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const descriptionText = text || 'No description available.';
    const needsToggle = descriptionText.length > maxLength;

    if (!needsToggle) {
        return <p className="download-description">{descriptionText}</p>;
    }

    const displayTruncatedText = descriptionText.substring(0, maxLength).trim();
    const visibleText = isExpanded ? descriptionText : displayTruncatedText;

    return (
        <div className="description-container">
            <p className="download-description">
                {visibleText}
                {!isExpanded && '... '}
                <button
                    className="toggle-description-button inline-toggle" 
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded(prev => !prev);
                    }}
                >
                    {isExpanded ? 'Show Less' : 'Show More'}
                </button>
            </p>
        </div>
    );
};

// ------------------------------------------

const Download = () => {
    const { api, API_BASE_URL } = useApi();
    
    const [groupedDownloads, setGroupedDownloads] = useState({});
    const [selectedDevice, setSelectedDevice] = useState('');
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [focusedDownload, setFocusedDownload] = useState(null);

    const history = useHistory();
    const location = useLocation();

    // 1. Ambil data dari API menggunakan 'api' instance (Axios)
    useEffect(() => {
        const fetchDownloads = async () => {
            if (!API_BASE_URL) return;

            try {
                // Menggunakan 'api' supaya withCredentials & interceptor berfungsi
                const response = await api.get('/downloads');
                const data = response.data;

                const grouped = data.reduce((acc, item) => {
                    const device = item.device_type || 'Other';
                    if (!acc[device]) acc[device] = [];
                    acc[device].push(item);
                    return acc;
                }, {});

                setGroupedDownloads(grouped);
                const devices = Object.keys(grouped);
                setSelectedDevice(devices[0] || '');
            } catch (err) {
                setError(err.response?.data?.message || err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchDownloads();
    }, [api, API_BASE_URL]);

    // 2. Monitor URL parameter
    useEffect(() => {
        const allItems = Object.values(groupedDownloads).flat();
        if (allItems.length === 0) return;

        const rawParam = location.search.substring(1); 

        if (rawParam) {
            const appNameFromUrl = decodeURIComponent(rawParam).replace(/-/g, ' ');
            const foundItem = allItems.find(
                item => item.application_name.trim().toLowerCase() === appNameFromUrl.trim().toLowerCase()
            );

            if (foundItem) {
                setFocusedDownload(foundItem);
                if (foundItem.device_type && foundItem.device_type !== selectedDevice) {
                    setSelectedDevice(foundItem.device_type);
                }
            } else {
                setFocusedDownload(null);
            }
        } else {
            setFocusedDownload(null);
        }
    }, [location.search, groupedDownloads, selectedDevice]);

    const handleOpenFocus = (item) => {
        const appSlug = encodeURIComponent(item.application_name.replace(/\s+/g, '-'));
        history.push({ search: `?${appSlug}` });
    };

    const handleCloseFocus = () => {
        history.push({ search: '' });
    };

    const downloads = groupedDownloads[selectedDevice] || [];
    const displayed = expanded ? downloads : downloads.slice(0, 6);

    if (loading) return <p style={{ color: 'white', textAlign: 'center' }}>Loading...</p>;
    if (error) return <p style={{ color: 'red', textAlign: 'center' }}>Error: {error}</p>;

    return (
        <div className="Download">
            <h1>Download Center</h1>
            
            <div className="category-bar">
                {Object.keys(groupedDownloads).map(device => (
                    <button
                        key={device}
                        className={`category-button ${device === selectedDevice ? 'active' : ''}`}
                        onClick={() => {
                            setSelectedDevice(device);
                            setExpanded(false);
                            if (location.search) history.push({ search: '' });
                        }}
                    >
                        {device}
                    </button>
                ))}
            </div>

            <div className="download-list">
                {displayed.map(item => {
                    const descriptionToDisplay = item.description || item.apk_description || 'No description available.';
                    return (
                        <div key={item.id} className="download-card" onClick={() => handleOpenFocus(item)} style={{ cursor: 'pointer' }}>
                            <div className="image-wrapper">
                                <img
                                    src={item.image_link || 'https://bit.ly/product_image_default'}
                                    alt={item.application_name}
                                    className="download-image"
                                />
                            </div>
                            <div className="download-details">
                                <h2>{item.application_name}</h2>
                                <p className="download-version">Version: {item.apk_version}</p>
                                <DescriptionToggle text={descriptionToDisplay} maxLength={25} />
                                <a
                                    href={item.download_link}
                                    className="download-button"
                                    download
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Download
                                </a>
                            </div>
                        </div>
                    );
                })}
            </div>

            {downloads.length > 6 && (
                <div className="see-more-wrapper">
                    <button className="see-more-button" onClick={() => setExpanded(prev => !prev)}>
                        {expanded ? 'See Less' : 'See More'}
                    </button>
                </div>
            )}

            {focusedDownload && (
                <div className="download-focus-overlay" onClick={handleCloseFocus}>
                    <div className="download-focus-inframe" onClick={(e) => e.stopPropagation()}>
                        <button className="close-focus-btn" onClick={handleCloseFocus}>&times;</button>
                        <div className="focus-content-layout">
                            <div className="focus-image-section">
                                <img 
                                    src={focusedDownload.image_link || 'https://bit.ly/product_image_default'} 
                                    alt={focusedDownload.application_name} 
                                />
                            </div>
                            <div className="focus-details-section">
                                <h2>{focusedDownload.application_name}</h2>
                                <p className="focus-version-tag">Version: {focusedDownload.apk_version || 'N/A'}</p>
                                <p className="focus-full-description">
                                    {focusedDownload.description || focusedDownload.apk_description || 'No description available.'}
                                </p>
                                <div className="focus-action-box">
                                    <div className="focus-download-row">
                                        <div className="device-badge">
                                            Platform: <span>{focusedDownload.device_type || 'Android'}</span>
                                        </div>
                                        <a 
                                            href={focusedDownload.download_link} 
                                            className="focus-action-download-btn"
                                            download
                                        >
                                            Start Download
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Download;